/* Tiny one-shot loader for the Google Maps JS API.
   Multiple components can call useGoogleMaps() — only one script tag ever loads.
   Returns { ready, error } so consumers can guard their autocomplete attach. */
import { useEffect, useState } from "react";

let loaderPromise = null;

function loadScript(apiKey) {
    if (loaderPromise) return loaderPromise;
    if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
    if (window.google && window.google.maps && window.google.maps.places) {
        loaderPromise = Promise.resolve();
        return loaderPromise;
    }
    loaderPromise = new Promise((resolve, reject) => {
        const callbackName = `__gmapsReady_${Date.now()}`;
        window[callbackName] = () => {
            delete window[callbackName];
            resolve();
        };
        const s = document.createElement("script");
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}&v=weekly`;
        s.async = true;
        s.defer = true;
        s.onerror = (e) => reject(e);
        document.head.appendChild(s);
    });
    return loaderPromise;
}

export default function useGoogleMaps() {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        if (!apiKey) {
            setError(new Error("REACT_APP_GOOGLE_MAPS_API_KEY missing"));
            return;
        }
        let cancelled = false;
        loadScript(apiKey)
            .then(() => { if (!cancelled) setReady(true); })
            .catch((e) => { if (!cancelled) setError(e); });
        return () => { cancelled = true; };
    }, [apiKey]);

    return { ready, error };
}
